import {
  Breadcrumbs,
  Card,
  Typography,
  Grid,
  Input,
  Stack,
  FormLabel,
  Box,
  CircularProgress,
} from '@mui/joy';
import { AxiosError } from 'axios';
import { BuyTable } from '@/components/Pages/Buys/BuyTable';
import { CreateEditSupplierModal } from '@/components/Pages/Suppliers/CreateEditSupplierModal';
import { createFileRoute, Link } from '@tanstack/react-router';
import { DeleteSupplierModal } from '@/components/Pages/Suppliers/DeleteSupplierModal';
import { QueryResponse, SupplierResponseDTO } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/axios';

export const Route = createFileRoute('/_protected/suppliers/$id')({
  component: SupplierDetail,
});

function SupplierDetail() {
  const params = Route.useParams();
  const id = params.id;
  const { isAdmin, isSuperAdmin } = useAuth();
  const showActions = isAdmin || isSuperAdmin;

  const { data: supplier, isLoading } = useQuery<
    QueryResponse<SupplierResponseDTO>,
    AxiosError,
    SupplierResponseDTO
  >({
    queryKey: ['suppliers', id],
    queryFn: () => api.get(`/supplier/${id}`),
    enabled: !!id,
    select: ({ data }) => data.data,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!supplier) {
    return <div>Proveedor no encontrado</div>;
  }

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' mb={2}>
        <Breadcrumbs size='sm'>
          <Link to='/suppliers'>Proveedores</Link>
          <Typography>{supplier.name}</Typography>
        </Breadcrumbs>

        {showActions && (
          <Stack direction='row' gap={2}>
            <CreateEditSupplierModal supplier={supplier} />
            <DeleteSupplierModal
              id={supplier.id.toString()}
              name={supplier.name}
            />
          </Stack>
        )}
      </Stack>

      <Card sx={{ mb: 3 }}>
        <Typography level='h4' sx={{ mb: 2 }}>
          Información del Proveedor
        </Typography>
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <FormLabel>ID</FormLabel>
            <Input value={supplier.id} disabled />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Nombre</FormLabel>
            <Input value={supplier.name} disabled />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Email</FormLabel>
            <Input value={supplier.email} disabled />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Teléfono</FormLabel>
            <Input value={supplier.phoneNumber} disabled />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Dirección</FormLabel>
            <Input value={supplier.address} disabled />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Cuenta corriente</FormLabel>
            <Input
              value={(supplier.currentAcount ?? 0).toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                style: 'currency',
                currency: 'ARS',
              })}
              disabled
            />
          </Grid>
        </Grid>
      </Card>

      <Card>
        <Typography level='h4' sx={{ mb: 2 }}>
          Órdenes de Compra
        </Typography>
        <BuyTable
          showSupplierColumn={false}
          emptyMessage='No hay órdenes de compra para este proveedor'
          errorTitle='Error al cargar las órdenes de compra'
          supplierId={parseInt(id)}
          showFilters={false}
        />
      </Card>
    </Box>
  );
}
